if (!customElements.get('product-personalization')) {
  customElements.define(
    'product-personalization',
    class ProductPersonalization extends HTMLElement {
      static NAME_KEY = 'properties[Name]';
      static NUMBER_KEY = 'properties[Number]';

      constructor() {
        super();

        this.nameInput = this.querySelector('[data-personalization-field="Name"]');
        this.numberInput = this.querySelector('[data-personalization-field="Number"]');

        this.nameInput?.addEventListener('input', this.onNameInput);
        this.numberInput?.addEventListener('input', this.onNumberInput);
        this.nameInput?.addEventListener('blur', this.onNameBlur);
        this.numberInput?.addEventListener('blur', this.onNumberBlur);

        this.syncFieldNames();
      }

      connectedCallback() {
        // Buy buttons render the <form> AFTER this block. During innerHTML upgrades
        // (Quick Add, variant swap), this element can connect before its form exists,
        // or document.getElementById() can resolve the previous form still on the page.
        // Listen on document and match the live form= id instead of caching a form node.
        document.addEventListener('submit', this.onSubmitCapture, true);
        document.addEventListener('formdata', this.onFormData, true);
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmitCapture, true);
        document.removeEventListener('formdata', this.onFormData, true);
      }

      get formId() {
        return this.nameInput?.getAttribute('form') || this.numberInput?.getAttribute('form') || '';
      }

      isOwnForm(form) {
        return Boolean(form && this.formId && form.id === this.formId);
      }

      cleanName(value) {
        return String(value).replace(/[^\p{L}\p{M}\s'\u2019-]/gu, '');
      }

      cleanNumber(value) {
        return String(value).replace(/\D/g, '');
      }

      onNameInput = (event) => {
        const cleaned = this.cleanName(event.target.value);
        if (event.target.value !== cleaned) event.target.value = cleaned;
        this.syncFieldName(this.nameInput, this.constructor.NAME_KEY);
      };

      onNumberInput = (event) => {
        const cleaned = this.cleanNumber(event.target.value);
        if (event.target.value !== cleaned) event.target.value = cleaned;
        this.syncFieldName(this.numberInput, this.constructor.NUMBER_KEY);
      };

      onNameBlur = () => {
        if (!this.nameInput) return;
        this.nameInput.value = this.nameInput.value.trim();
        this.syncFieldName(this.nameInput, this.constructor.NAME_KEY);
      };

      onNumberBlur = () => {
        if (!this.numberInput) return;
        this.numberInput.value = this.numberInput.value.trim();
        this.syncFieldName(this.numberInput, this.constructor.NUMBER_KEY);
      };

      prepareFields() {
        if (this.nameInput) {
          this.nameInput.value = this.cleanName(this.nameInput.value).trim();
        }
        if (this.numberInput) {
          this.numberInput.value = this.cleanNumber(this.numberInput.value).trim();
        }
        this.syncFieldNames();
      }

      syncFieldNames() {
        this.syncFieldName(this.nameInput, this.constructor.NAME_KEY);
        this.syncFieldName(this.numberInput, this.constructor.NUMBER_KEY);
      }

      syncFieldName(input, propertyKey) {
        if (!input) return;

        // Nameless controls are skipped by FormData and native submit, so blank
        // personalization does not create empty line item properties.
        if (input.value.trim()) {
          input.setAttribute('name', propertyKey);
        } else {
          input.removeAttribute('name');
        }
      }

      onSubmitCapture = (event) => {
        if (!this.isOwnForm(event.target)) return;
        this.prepareFields();
      };

      onFormData = (event) => {
        if (!this.isOwnForm(event.target)) return;

        this.prepareFields();
        this.syncProperty(event.formData, this.constructor.NAME_KEY, (value) => this.cleanName(value).trim());
        this.syncProperty(event.formData, this.constructor.NUMBER_KEY, (value) => this.cleanNumber(value).trim());
      };

      syncProperty(formData, key, cleaner) {
        const raw = formData.get(key);
        if (raw == null) return;

        const cleaned = cleaner(String(raw));
        if (cleaned) {
          formData.set(key, cleaned);
        } else {
          formData.delete(key);
        }
      }
    }
  );
}
